import { createRoute } from "@tanstack/solid-router";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { ensureTabAccount } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@cashsync/shared";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

function AppLayout() {
  return <AppLayoutContent />;
}

let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: if the user explicitly chose the GUEST account, do NOT verify
  // with the server.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout verifying session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

interface AppLayoutSearch {
  account?: "switch";
}

function validateAppLayoutSearch(search: Record<string, unknown>): AppLayoutSearch {
  return {
    account: search.account === "switch" ? "switch" : undefined,
  };
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: validateAppLayoutSearch,
  beforeLoad: () => {
    const accountsState = readAccountsStateSync();
    if (accountsState.accounts.length === 0) {
      void accountsStore.ensureGuestActive();
    }
    ensureTabAccount(accountsState);

    const snapshot = sessionActor.getSnapshot();

    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: AppLayout,
});
